/**
 * Turns a Markdown ticket description into a plain-text snippet for the clamped list-row preview.
 *
 * The ticket data itself is never touched — this only reshapes what the 2-line clamp shows, so a
 * row reads "Öncelik: High Label: NLP" instead of "**Öncelik:** High **Label:** NLP". The full
 * Markdown is still rendered as-is in the ticket viewer/thread.
 *
 * This is a preview clamp, not a Markdown renderer: it does not aim for CommonMark-exact behaviour
 * (nested/unbalanced markers are resolved "sensibly", not spec-perfectly), and every pattern below
 * uses a fixed literal delimiter with a bounded, non-nested character class so matching stays
 * linear in the input length — no `(x+)+`-style construct that could backtrack catastrophically.
 */

// Underscore emphasis must not consume underscores that are part of an identifier
// (`snake_case_name`, `MusteriListesi_v2`): only treat `_x_` / `__x__` as emphasis when the
// delimiter is NOT flanked by a word character on its outer side.
const NOT_WORD_BEFORE: string = "(?<![A-Za-z0-9_])";
const NOT_WORD_AFTER: string = "(?![A-Za-z0-9_])";

const FENCE_LINE_RE: RegExp = /^\s*(```|~~~)/;
const TABLE_SEPARATOR_RE: RegExp = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;
const HORIZONTAL_RULE_RE: RegExp = /^\s*([-*_])\s*(?:\1\s*){2,}$/;
const HEADING_RE: RegExp = /^#{1,6}\s+/;
const BLOCKQUOTE_RE: RegExp = /^(?:>\s?)+/;
const LIST_MARKER_RE: RegExp = /^\s*(?:[-*+]|\d+\.)\s+/;

const IMAGE_RE: RegExp = /!\[([^\]]*)\]\([^)]*\)/g;
const LINK_RE: RegExp = /\[([^\]]*)\]\([^)]*\)/g;
const AUTOLINK_RE: RegExp = /<((?:https?|ftp):\/\/[^\s>]+)>/g;
const HTML_TAG_RE: RegExp = /<\/?[A-Za-z][^<>]*>/g;
const INLINE_CODE_RE: RegExp = /`([^`\n]*)`/g;
const STRIKETHROUGH_RE: RegExp = /~~([\s\S]+?)~~/g;
const BOLD_STAR_RE: RegExp = /\*\*([\s\S]+?)\*\*/g;
const BOLD_UNDERSCORE_RE: RegExp = new RegExp(`${NOT_WORD_BEFORE}__(?!_)([\\s\\S]+?)(?<!_)__${NOT_WORD_AFTER}`, "g");
const ITALIC_STAR_RE: RegExp = /\*([^*\n]+?)\*/g;
const ITALIC_UNDERSCORE_RE: RegExp = new RegExp(`${NOT_WORD_BEFORE}_(?!_)([^_\\n]+?)_${NOT_WORD_AFTER}`, "g");
const WHITESPACE_RE: RegExp = /\s+/g;

/** Strips a single Markdown line-start construct (heading/blockquote/list marker) and table pipes. */
function stripLineLevelMarkup(line: string): string {
  if (HORIZONTAL_RULE_RE.test(line) || TABLE_SEPARATOR_RE.test(line)) return "";

  let result: string = line;
  result = result.replace(HEADING_RE, "");
  result = result.replace(BLOCKQUOTE_RE, "");
  result = result.replace(LIST_MARKER_RE, "");
  // Content table rows: turn pipes into spaces rather than deleting them, so cell text stays
  // separated ("| A | B |" -> "A B", not "AB").
  result = result.replace(/\|/g, " ");
  return result;
}

/** Removes fenced code delimiters line-by-line, keeping the code text itself untouched. */
function stripCodeFences(text: string): string {
  const lines: string[] = text.split("\n");
  const kept: string[] = [];

  // An unterminated fence (no closing ``` / ~~~) still just drops the opening marker and keeps
  // every following line as plain text — nothing more to reconcile.
  for (const line of lines) {
    if (FENCE_LINE_RE.test(line)) continue;
    kept.push(line);
  }

  return kept.join("\n");
}

function stripInlineMarkup(text: string): string {
  let result: string = text;
  result = result.replace(IMAGE_RE, "$1");
  result = result.replace(LINK_RE, "$1");
  result = result.replace(AUTOLINK_RE, "$1");
  // Raw HTML tags (`<br>`, `<b>`, `</b>`, …) become a space, not an empty string, so removing one
  // never glues the words on either side together ("Line one<br>Line two" -> "Line one Line two").
  result = result.replace(HTML_TAG_RE, " ");
  result = result.replace(INLINE_CODE_RE, "$1");
  result = result.replace(STRIKETHROUGH_RE, "$1");
  result = result.replace(BOLD_STAR_RE, "$1");
  result = result.replace(BOLD_UNDERSCORE_RE, "$1");
  result = result.replace(ITALIC_STAR_RE, "$1");
  result = result.replace(ITALIC_UNDERSCORE_RE, "$1");
  return result;
}

/**
 * Reduces a Markdown ticket description to plain text suitable for a clamped preview line.
 * Safe on Turkish text and emoji, and safe on large input (linear-pattern regexes only).
 */
export function stripMarkdownPreview(text: string | undefined): string {
  if (!text) return "";

  const withoutFences: string = stripCodeFences(text);
  const linesWithoutBlockMarkup: string[] = withoutFences.split("\n").map(stripLineLevelMarkup);
  const joined: string = linesWithoutBlockMarkup.join(" ");
  const withoutInlineMarkup: string = stripInlineMarkup(joined);

  return withoutInlineMarkup.replace(WHITESPACE_RE, " ").trim();
}
